import React, {useEffect, useRef, useState} from 'react';
import {Redirect, useHistory, useParams} from "react-router-dom";
import {observer} from "mobx-react-lite";
import {Button, Drawer, Layout, Menu, Tag} from 'antd';
import {LogoutOutlined, MenuOutlined} from "@ant-design/icons";
import {authModel} from "../../entities/auth";
import {routeNames} from "../../shared/router/routeNames";
import {defaultSectionFor, sectionGroupsFor, sectionsFor} from "./sections";
import {SectionAccessContext} from "./sectionAccess";
import "./AdminPage.scss";

const menuItemsFor = (auth) => sectionGroupsFor(auth).map((group) => ({
  type: 'group',
  key: group.title,
  label: group.title,
  // no tooltips over the narrow menu: it opens itself on hover
  children: group.sections.map(({key, title, icon: Icon}) => ({key, label: title, icon: <Icon/>, title: false})),
}))

// One section at a time, picked in the menu on the left (a drawer on narrow screens); the URL keeps it.
// The menu holds the sections the user's roles open; a section opened with «просмотр» shows nothing to change.
// The menu is narrow (icons) and opens over the page while the mouse is on it (the owner, 28.10: «левое меню
// сворачивалось и разворачивалось при наведении»), so the page keeps its width.
export const AdminPage = observer(() => {
  const {section} = useParams()
  const history = useHistory()
  const [menuOpened, setMenuOpened] = useState(false)
  const [menuWide, setMenuWide] = useState(false)
  const widenTimer = useRef(null)
  useEffect(() => () => clearTimeout(widenTimer.current), [])
  const current = sectionsFor(authModel).find((s) => s.key === section)
  const brand = authModel.isAdmin ? 'админка' : 'панель'

  if (!current) {
    const first = defaultSectionFor(authModel)
    return first ? <Redirect to={`${routeNames.ADMIN}/${first}`}/> : null
  }
  const canEdit = authModel.canEdit(current.right)
  const {impersonation} = authModel

  // «Смотреть как»: back to one's own session (the tab closes when the browser lets it)
  const endImpersonation = () => {
    authModel.logout()
    window.close()
    window.location.assign(routeNames.ADMIN)
  }

  // a short pause, so the mouse passing by does not open it
  const widen = (wide) => {
    clearTimeout(widenTimer.current)
    widenTimer.current = setTimeout(() => setMenuWide(wide), wide ? 120 : 250)
  }

  const open = ({key}) => {
    setMenuOpened(false)
    if (key !== section) history.push(`${routeNames.ADMIN}/${key}`)
  }
  const menu = <Menu mode='inline' selectedKeys={[current.key]} items={menuItemsFor(authModel)} onClick={open} className='admin-menu'/>
  const Section = current.component

  return (
    <Layout className='admin-layout'>
      <div className='admin-sider-space'/>
      <Layout.Sider
        width={256}
        collapsedWidth={80}
        collapsed={!menuWide}
        trigger={null}
        theme='light'
        className={`admin-sider${menuWide ? ' admin-sider--wide' : ''}`}
        onMouseEnter={() => widen(true)}
        onMouseLeave={() => widen(false)}
        onFocus={() => widen(true)}
        onBlur={() => widen(false)}
      >
        <div className='admin-brand'>ЕЦИ <span>{brand}</span></div>
        {menu}
      </Layout.Sider>
      <Drawer
        placement='left'
        width={260}
        visible={menuOpened}
        onClose={() => setMenuOpened(false)}
        bodyStyle={{padding: 0}}
        title={`ЕЦИ · ${brand}`}
      >
        {menu}
      </Drawer>
      <Layout.Content className='admin-content'>
        {impersonation && (
          <div className='impersonation-bar'>
            <span>Вы смотрите админку как <b>{impersonation.user.name}</b> — только просмотр</span>
            <Button size='small' onClick={endImpersonation}>Закрыть</Button>
          </div>
        )}
        <header className='admin-header'>
          <Button className='admin-menu-button' icon={<MenuOutlined/>} onClick={() => setMenuOpened(true)} aria-label='Меню'/>
          <div className='admin-header__text'>
            <h1>{current.title}{!canEdit && <Tag className='view-only-tag'>только просмотр</Tag>}</h1>
            {current.hint && <p>{current.hint}</p>}
          </div>
          <Button icon={<LogoutOutlined/>} onClick={() => authModel.logout()}>Выйти</Button>
        </header>
        <div className='admin-section'>
          <SectionAccessContext.Provider value={{canEdit}}>
            <Section key={current.key}/>
          </SectionAccessContext.Provider>
        </div>
      </Layout.Content>
    </Layout>
  );
})
